import { Button, Icon, IconPack } from '@healingpaper/cell'
import Lottie, { LottieRefCurrentProps } from 'lottie-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import likeAnimation from './assets/likeAnimation.json'
import { fadeIn, fadeOut } from './keyframes'

import Tooltip from '@/components/atomic/molecules/Tooltip'

type TooltipAnimation = 'none' | 'fadeIn' | 'fadeOut'

const delay = (ms: number) => {
  return new Promise<NodeJS.Timeout>((resolve) => {
    const timeout = setTimeout(() => {
      resolve(timeout)
    }, ms)
  })
}

const SHOW_ANIMATION_TIME_MS = 500
const HIDE_ANIMATION_TIME_MS = 300
const WAIT_TIME_MS = 100

export interface Props {
  id?: string
  className?: string
  isShowWishGuideTooltip?: boolean
  text: {
    wishGuide: string
    wishlistTitle: string
    goToWishlistButton: string
  }
  initWish: () => Promise<boolean>
  onClickWish: (isWish: boolean) => void
  onClickButton: () => void
}

const WishButton = ({ id, className, text, initWish, onClickWish, onClickButton }: Props): JSX.Element => {
  const likeLottieRef = useRef<LottieRefCurrentProps>(null)
  const [isWish, setIsWish] = useState<boolean | null>(null)
  const [isShowWishlistTooltip, setIsShowWishlistTooltip] = useState(false)
  const [isShowWishGuideTooltip, setIsShowWishGuideTooltip] = useState(false)
  const [isShowLottie, setIsShowLottie] = useState(false)

  const [wishGuideTooltipAnimation, setWishGuideTooltipAnimation] = useState<TooltipAnimation>('none')
  const [wishListTooltipAnimation, setWishListTooltipAnimation] = useState<TooltipAnimation>('none')

  const showWishGuideTooltip = useCallback(async () => {
    if (isShowWishGuideTooltip) {
      return
    }

    setWishGuideTooltipAnimation('fadeIn')
    await delay(WAIT_TIME_MS)
    setIsShowWishGuideTooltip(true)
  }, [isShowWishGuideTooltip])

  const hideWishGuideTooltip = useCallback(
    async (hideForce?: boolean) => {
      if (!hideForce && !isShowWishGuideTooltip) {
        return
      }

      setWishGuideTooltipAnimation('fadeOut')
      await delay(HIDE_ANIMATION_TIME_MS)
      setIsShowWishGuideTooltip(false)
      await delay(WAIT_TIME_MS)
      setWishListTooltipAnimation('none')
    },
    [isShowWishGuideTooltip],
  )

  const showWishListTooltip = useCallback(async () => {
    if (isShowWishlistTooltip) {
      return
    }

    setWishListTooltipAnimation('fadeIn')
    await delay(WAIT_TIME_MS)
    setIsShowWishlistTooltip(true)
  }, [isShowWishlistTooltip])

  const hideWishListTooltip = useCallback(async () => {
    if (!isShowWishlistTooltip) {
      return
    }

    setWishListTooltipAnimation('fadeOut')
    await delay(HIDE_ANIMATION_TIME_MS)
    setIsShowWishlistTooltip(false)
    await delay(WAIT_TIME_MS)
    setWishListTooltipAnimation('none')
  }, [isShowWishlistTooltip])

  const onWish = useCallback(async () => {
    if (isWish === null) {
      return
    }

    setIsShowLottie(true)
    likeLottieRef.current?.play()
    setIsWish(true)

    await hideWishGuideTooltip()
    await delay(WAIT_TIME_MS)
    await showWishListTooltip()

    localStorage.setItem('hideWishGuideTooltip', 'true')

    onClickWish(true)
  }, [isWish, hideWishGuideTooltip, showWishListTooltip, onClickWish])

  const onUnWish = useCallback(async () => {
    if (isWish === null) {
      return
    }

    likeLottieRef.current?.goToAndStop(0)
    setIsWish(false)

    isShowWishlistTooltip && (await hideWishListTooltip())

    onClickWish(false)
  }, [isWish, isShowWishlistTooltip, hideWishListTooltip, onClickWish])

  const initWishGuideTooltip = useCallback(async () => {
    const isShowWishGuideTooltip =
      isWish === false && localStorage && localStorage.getItem('hideWishGuideTooltip') !== 'true'

    if (isShowWishGuideTooltip) {
      await showWishGuideTooltip()
      await delay(5000)
      await hideWishGuideTooltip(true)
    }
  }, [isWish, showWishGuideTooltip, hideWishGuideTooltip])

  useEffect(() => {
    if (isWish !== null) {
      initWishGuideTooltip()
    }
  }, [isWish, initWishGuideTooltip])

  useEffect(() => {
    let isMounted = true
    const initializeWish = async () => {
      try {
        const wishState = await initWish()
        if (isMounted) {
          setIsWish(wishState)
        }
      } catch (error) {
        if (isMounted) {
          setIsWish(false)
        }
      }
    }
    initializeWish()
    return () => {
      isMounted = false
    }
  }, [initWish])

  return (
    <div className={`relative ${className}`}>
      <StyledWishButton
        id={id}
        disabled={isWish === null}
        aria-label="wish button"
        onClick={() => {
          if (isWish) {
            void onUnWish()
          } else {
            void onWish()
          }
        }}
      >
        {isWish === false && (
          <Icon
            name={IconPack.GENERAL_FEATURE_LIKE_BLACK}
            width={24}
            height={24}
            aria-label="inactive wish"
            color="black"
          />
        )}
        {isWish === true && (
          <Icon
            name={IconPack.GENERAL_FEATURE_LIKE_ON}
            width={24}
            height={24}
            aria-label="active wish"
            color="orange"
          />
        )}
      </StyledWishButton>

      <StyledLottie
        aria-label="active wish animation"
        style={{ display: !isShowLottie ? 'none' : 'block', pointerEvents: 'none' }} // 클릭 이벤트를 무시하도록 설정
        lottieRef={likeLottieRef}
        animationData={likeAnimation}
        loop={false}
        autoplay={false}
        onClick={() => {
          if (isWish) {
            void onUnWish()
          } else {
            void onWish()
          }
        }}
        onComplete={() => {
          setIsShowLottie(false)
        }}
      />

      <StyledTooltip
        isTooltipOpen={isShowWishGuideTooltip}
        $animation={wishGuideTooltipAnimation}
        label="wish guide tooltip"
        onClick={() => {}}
      >
        <p dangerouslySetInnerHTML={{ __html: text.wishGuide }} />
      </StyledTooltip>
      <StyledTooltip
        isTooltipOpen={isShowWishlistTooltip}
        $animation={wishListTooltipAnimation}
        label="wishlist tooltip"
        onClick={() => {}}
      >
        <div role="presentation">
          <h3>{text.wishlistTitle}</h3>
          <Button size="Medium" onClick={onClickButton}>
            {text.goToWishlistButton}
          </Button>
        </div>
      </StyledTooltip>
    </div>
  )
}

const StyledWishButton = styled.button`
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  padding: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const StyledLottie = styled(Lottie)`
  position: absolute;
  width: 48px;
  height: 48px;
  right: -10px;
  top: -10px;
`

const StyledTooltip = styled(Tooltip)<{ $animation: TooltipAnimation }>`
  position: absolute;
  z-index: 90;
  right: -6px;
  top: 20px;

  ${({ $animation }) => {
    switch ($animation) {
      case 'fadeIn':
        return css`
          animation-name: ${fadeIn};
          animation-duration: ${SHOW_ANIMATION_TIME_MS}ms;
          opacity: 1;
          transform: translate(0px, 0px);
        `
      case 'fadeOut':
        return css`
          animation-name: ${fadeOut};
          animation-duration: ${HIDE_ANIMATION_TIME_MS}ms;
          opacity: 0;
          transform: translate(0px, -5px);
        `
    }
  }}

  & > div[class*='StyledTooltipArea'] {
    position: fixed;
    left: auto;
    right: -9px;
    top: 15px;
    width: 100vw;
    height: 100vh;
  }

  & > div[class*='StyledTooltipWrap'] {
    box-shadow: none;
    left: auto;
    right: 0px;
    flex-direction: column;
    & > [class*='Arrow'] {
      width: 100%;
      height: 10px;
      top: -10px;
      &::after {
        box-shadow: none;
        left: auto;
        right: 7px;
      }
    }
    p {
      margin: 0px;
      ${({ theme }) => theme.typography.title4}
      font-weight: normal;
      strong {
        color: ${({ theme }) => theme.colors.orange};
      }
    }
    h3 {
      margin: 0px 0px 6px;
      ${({ theme }) => theme.typography.title4}
    }
  }
`

export default WishButton
